import { useRef } from 'react'
import { gsap, prefersReduced } from '../../lib/gsap.js'
import useIso from './useIso.js'

// Tween a number 0 -> countTo into ref.current.textContent, formatted each frame.
// Plays once, the first time countTo is a meaningful (non-zero) number — so a cold
// load where Firestore data streams in after mount still animates instead of
// playing 0->0 and locking out. Instant final under reduced motion.
//
// After that first play, every later countTo is written straight to the element.
// This must be explicit: the tween replaced the text node React rendered, so a
// re-render can no longer update the number on its own. Without this, Home's
// stats froze at their first value (no live updates), and a value that changed
// mid-tween (e.g. pipeline = engagements x stages, two snapshots) stuck at a
// partial count because the cleanup killed the tween and nothing set the final.
export default function useCountUp(ref, countTo, format = (n) => n, duration = 0.6) {
  const played = useRef(false)
  useIso(() => {
    const el = ref.current
    if (el == null || typeof countTo !== 'number') return
    if (played.current) { el.textContent = format(countTo); return }
    if (prefersReduced()) { played.current = true; el.textContent = format(countTo); return }
    if (countTo === 0) return // JSX already shows format(0); wait for a real value before animating (don't mark played)
    played.current = true
    const obj = { n: 0 }
    el.textContent = format(0) // squash the one-frame flash of the final value before the tween's first onUpdate
    const tween = gsap.to(obj, {
      n: countTo,
      duration,
      ease: 'power2.out',
      onUpdate: () => { el.textContent = format(Math.round(obj.n)) },
      onComplete: () => { el.textContent = format(countTo) },
    })
    return () => tween.kill()
  }, [countTo])
}
